import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck } from 'lucide-react';

const iconStroke = 1.75;

type Partner = {
  name: string;
  role: string;
  proof: string;
  logoSrc: string;
  logoAlt: string;
  logoClassName: string;
  logoLabel?: string;
};

const partners: Partner[] = [
  {
    name: 'Cloudflare',
    role: 'Global Hosting, Security, Infrastructure',
    proof: 'Edge hosting, routing, security controls and resilient platform infrastructure for the public website and application.',
    logoSrc: '/partner-logos/cloudflare-mark.png',
    logoAlt: 'Cloudflare logo',
    logoClassName: 'h-12 w-auto max-w-[15rem] object-contain',
  },
  {
    name: 'Google Cloud Run',
    role: 'AI Inference',
    proof: 'Dedicated processing capacity for heavy PDF analysis, rendering and AI-assisted remediation pipeline work.',
    logoSrc: '/partner-logos/google-cloud-run.svg',
    logoAlt: 'Google Cloud Run logo',
    logoClassName: 'h-12 w-12 object-contain',
    logoLabel: 'Google Cloud Run',
  },
  {
    name: 'Stripe',
    role: 'Payment Processing',
    proof: 'Secure checkout and payment processing for page-credit purchases and customer billing workflows.',
    logoSrc: '/partner-logos/stripe-wordmark-slate.svg',
    logoAlt: 'Stripe logo',
    logoClassName: 'h-10 w-auto max-w-[9rem] object-contain',
  },
];

type TechnologyPartnersProps = {
  className?: string;
  band?: boolean;
  compact?: boolean;
};

export default function TechnologyPartners({
  className = '',
  band = false,
  compact = false,
}: TechnologyPartnersProps) {
  return (
    <section
      className={`scroll-mt-24 ${band ? 'bg-slate-800' : 'bg-slate-900'} px-4 py-14 sm:px-6 lg:px-8 ${className}`}
      aria-labelledby="technology-partners-heading"
    >
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-300">
              Technology proof points
            </p>
            <h2 id="technology-partners-heading" className="mt-3 text-3xl font-semibold leading-tight text-white">
              Our Partners
            </h2>
            <p className="mt-4 text-base leading-7 text-slate-300">
              pdfaa.ai runs on trusted platforms for global hosting, secure infrastructure, AI inference and payment processing.
            </p>
          </div>
          <Link
            to="/security"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-300 hover:text-primary-200"
          >
            Review security model
            <ArrowRight className="h-4 w-4" strokeWidth={iconStroke} />
          </Link>
        </div>

        <div className={`mt-9 grid gap-5 ${compact ? 'lg:grid-cols-3' : 'md:grid-cols-3'}`}>
          {partners.map((partner) => (
            <article key={partner.name} className="rounded-lg border border-slate-700 bg-slate-950 p-6">
              <div className="flex min-h-20 items-center rounded-md bg-white px-4 py-3">
                <img
                  src={partner.logoSrc}
                  alt={partner.logoAlt}
                  loading="lazy"
                  className={partner.logoClassName}
                />
                {partner.logoLabel ? (
                  <span className="ml-3 text-xl font-semibold tracking-normal text-slate-900">
                    {partner.logoLabel}
                  </span>
                ) : null}
              </div>
              <div className="mt-6 flex items-start gap-3">
                <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary-300" strokeWidth={iconStroke} />
                <div>
                  <h3 className="text-lg font-semibold text-white">{partner.name}</h3>
                  <p className="mt-1 text-sm font-semibold leading-6 text-primary-200">{partner.role}</p>
                  <p className="mt-2 text-sm leading-6 text-slate-300">{partner.proof}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
